import { createAction } from 'redux-api-middleware'

import { HUBBLE_API_TOKEN } from '../../store/middleware/APIUrl'
import { IDUtils, DateUtils } from '../../utils'

const Types = {
    DESELECT_PLAYLIST: 'DESELECT_PLAYLIST',
    FETCH_PLAYLISTS_REQUEST: 'FETCH_PLAYLISTS##REQUEST',
    FETCH_PLAYLISTS_SUCCESS: 'FETCH_PLAYLISTS##SUCCESS',
    FETCH_PLAYLIST_SINGLE_REQUEST: 'FETCH_PLAYLIST_SINGLE##REQUEST',
    FETCH_PLAYLIST_SINGLE_SUCCESS: 'FETCH_PLAYLIST_SINGLE##SUCCESS',
    UPDATE_PLAYLIST_REQUEST: 'UPDATE_PLAYLIST##REQUEST',
    UPDATE_PLAYLIST_SUCCESS: 'UPDATE_PLAYLIST##SUCCESS',
    CREATE_PLAYLIST_REQUEST: 'CREATE_PLAYLIST##REQUEST',
    CREATE_PLAYLIST_SUCCESS: 'CREATE_PLAYLIST##SUCCESS',
    CREATE_PLAYLIST_CP_REQUEST: 'CREATE_PLAYLIST_CP##REQUEST',
    CREATE_PLAYLIST_CP_SUCCESS: 'CREATE_PLAYLIST_CP##SUCCESS',
    DESTROY_PLAYLIST_REQUEST: 'DESTROY_PLAYLIST##REQUEST',
    DESTROY_PLAYLIST_SUCCESS: 'DESTROY_PLAYLIST##SUCCESS',
    DESTROY_PLAYLIST_FAILURE: 'DESTROY_PLAYLIST##FAILURE',
    EDIT_PLAYLIST_LAYER_ARRAY: 'EDIT_PLAYLIST_LAYER_ARRAY',
    EDIT_PLAYLIST_MEDIA_ARRAY: 'EDIT_PLAYLIST_MEDIA_ARRAY',
    CHANGE_PLAYLISTS: 'CHANGE_PLAYLISTS',
    CHANGE_SELECTED_PLAYLIST: 'CHANGE_SELECTED_PLAYLIST',
    CHANGE_SELECTED_MEDIA: 'CHANGE_SELECTED_MEDIA',
    API_CALL_FAILURE: 'API_CALL##FAILURE',
}

/**
 * @deprecated
 */
const PlaylistsActions = Types

/**
 * Fetch playlists data
 * @param {Number} [page]
 * @param {object} [opts] 
 * @param {number[]} [opts.chains]
 * @param {string} [opts.name]
 */
function fetchPlaylists(page, { chains, name } = {}) {
    const params = {}

    if (page > 0) {
        params.page = page
    }

    if (chains) {
        params.ch = chains
    }

    if (name) {
        params.name = name
    }

    const query = new URLSearchParams(params).toString()

    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/playlists?${query}`,
        method: 'GET',
        types: [
            Types.FETCH_PLAYLISTS_REQUEST,
            {
                type: Types.FETCH_PLAYLISTS_SUCCESS,
                meta: { noFirstPage: page > 1 },
            },
            Types.API_CALL_FAILURE,
        ],
        options: {
            abortable: true,
        },
    })
}

/**
 * Fetch single playlist data
 * @param {Number} id
 */
function fetchSinglePlaylist(id) {
    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/playlists/${id}`,
        method: 'GET',
        types: [Types.FETCH_PLAYLIST_SINGLE_REQUEST, Types.FETCH_PLAYLIST_SINGLE_SUCCESS, Types.API_CALL_FAILURE],
    })
}

/**
 * Deselects the selected playlist
 */
function deselectPlaylist() {
    return {
        type: Types.DESELECT_PLAYLIST,
    }
}

/**
 * Update playlist record
 * @param {Number} id
 * @param {Object} playlist
 */
function updatePlaylist(id, playlist) {
    const { layers: layersRaw, media: mediaRaw, date_end, date_start } = playlist

    let layers = null
    let media = null

    // Transform layer array into an array of layer ids
    if (layersRaw) layers = layersRaw.map(({ id: layerID }) => layerID)

    // Transform media array into an array with the form [media-id, order]
    if (mediaRaw) media = mediaRaw.map(({ id: mediaID }, index) => [mediaID, index]).reduce((a, b) => [...a, b], [])
    const data = {
        ...playlist,
        layers,
        media,
        date_end: DateUtils.formatDB(date_end),
        date_start: DateUtils.formatDB(date_start),
    }

    const body = JSON.stringify(data)

    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/playlists/${id}`,
        method: 'PUT',
        types: [
            Types.UPDATE_PLAYLIST_REQUEST,
            {
                type: Types.UPDATE_PLAYLIST_SUCCESS,
                meta: { withArray: !!layers || !!media, id },
            },
            Types.API_CALL_FAILURE,
        ],
        body,
    })
}

/**
 * Store playlist
 * @param {Object} playlist
 */
function createPlaylist(playlist) {
    const { date_end, date_start } = playlist
    const body = JSON.stringify({
        ...playlist,
        date_end: DateUtils.formatDB(date_end),
        date_start: DateUtils.formatDB(date_start),
    })

    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/playlists`,
        method: 'POST',
        types: [Types.CREATE_PLAYLIST_REQUEST, Types.CREATE_PLAYLIST_SUCCESS, Types.API_CALL_FAILURE],
        body,
    })
}

/**
 * Create playlist for content push
 * @param {String} name
 */
function createPlaylistCP(name) {
    const body = JSON.stringify({
        name: name || IDUtils.generateID(),
        priority: 0,
    })

    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/playlists`,
        method: 'POST',
        types: [Types.CREATE_PLAYLIST_CP_REQUEST, Types.CREATE_PLAYLIST_CP_SUCCESS, Types.API_CALL_FAILURE],
        body,
    })
}

/**
 * Destroy content push playlist
 */
function destroyPlaylistCP() {
    return (dispatch, getState) => {
        const state = getState()
        const { content } = state || {}
        const { contentPushContent } = content || {}
        const { playlist } = contentPushContent || {}
        const { id } = playlist || {}

        if (!id) {
            return Promise.resolve()
        }

        return createAction({
            endpoint: `${HUBBLE_API_TOKEN}/playlists/${id}`,
            method: 'DELETE',
            types: [Types.DESTROY_PLAYLIST_REQUEST, Types.DESTROY_PLAYLIST_SUCCESS, Types.DESTROY_PLAYLIST_FAILURE],
        })
    }
}

const editPlaylistLayersArray = (array) => ({
    type: Types.EDIT_PLAYLIST_LAYER_ARRAY,
    payload: array,
})

const editPlaylistMediaArray = (array) => ({
    type: Types.EDIT_PLAYLIST_MEDIA_ARRAY,
    payload: array,
})

const changePlaylistsArray = (playlists) => ({
    type: Types.CHANGE_PLAYLISTS,
    payload: playlists,
})

const changeSelectedMediaArray = (media) => ({
    type: Types.CHANGE_SELECTED_MEDIA,
    payload: media,
})

const changeSelectedPlaylist = (channel) => ({
    type: Types.CHANGE_SELECTED_PLAYLIST,
    payload: channel,
})

export {
    Types,
    PlaylistsActions,
    changePlaylistsArray,
    changeSelectedMediaArray,
    changeSelectedPlaylist,
    createPlaylist,
    createPlaylistCP,
    deselectPlaylist,
    destroyPlaylistCP,
    editPlaylistLayersArray,
    editPlaylistMediaArray,
    fetchPlaylists,
    fetchSinglePlaylist,
    updatePlaylist,
}
